import { loadData } from './data/data-loader.js';
import { DataProcessor } from './data/data-processor.js';
import { MapRenderer } from './map/map-renderer.js';
import { HeatmapLayer } from './map/heatmap.js';
import { Timeline } from './temporal/timeline.js';
import { Animator } from './temporal/animator.js';
import { Search } from './search/search.js';
import { Statistics } from './stats/statistics.js';
import { VideoPanel } from './ui/video-panel.js';

class InteractiveMapper {
  constructor() {
    this.state = {
      currentYear: null,
      windowSize: 10,
      isPlaying: false,
      visibleLayers: new Set(['metadata1', 'metadata2'])
    };
  }

  async loadVersionInfo() {
    try {
      const response = await fetch('./version.json');
      if (response.ok) {
        const versionData = await response.json();
        const versionElement = document.getElementById('version-info');
        if (versionElement) {
          const buildDate = new Date(versionData.buildDate).toLocaleDateString();
          versionElement.textContent = `Version ${versionData.version} (${buildDate})`;
        }
      }
    } catch (error) {
      // Fallback if version.json doesn't exist (dev mode)
      const versionElement = document.getElementById('version-info');
      if (versionElement) {
        versionElement.textContent = 'Development version';
      }
    }
  }

  async init() {
    try {
      // Load version info
      this.loadVersionInfo();

      // Load all data
      console.log('Loading data...');
      const data = await loadData();

      this.dataProcessor = new DataProcessor(data);

      // Get year range from data
      const yearRange = this.dataProcessor.getYearRange();
      this.state.currentYear = yearRange[0];

      // Initialize components
      this.map = new MapRenderer('#map', data.locations);
      await this.map.init();

      this.heatmap1 = new HeatmapLayer(this.map, 'metadata1', '#ff6464');
      this.heatmap2 = new HeatmapLayer(this.map, 'metadata2', '#6496ff');

      // Set up heatmap click handlers
      this.heatmap1.onLocationClick((locationName, metadata) =>
        this.onLocationClick(locationName, metadata)
      );
      this.heatmap2.onLocationClick((locationName, metadata) =>
        this.onLocationClick(locationName, metadata)
      );

      // Initialize video panel
      this.videoPanel = new VideoPanel('video-panel');

      // Set up map update callback for re-rendering heatmaps
      this.map.setUpdateCallback(() => this.render());

      this.animator = new Animator(
        yearRange,
        (year) => this.onTimelineChange(year)
      );

      this.search = new Search('#search-input', '#search-results', data.locations);
      this.search.on('select', (location) => this.onLocationSelect(location));

      this.stats = new Statistics('#stats-charts', this.dataProcessor);
      // Listen to brush changes from the statistics chart
      this.stats.on('change', (data) => this.onBrushChange(data));

      // Set up event listeners
      this.setupEventListeners();

      // Initial render
      this.render();

      console.log('Interactive Mapper initialized successfully');
    } catch (error) {
      console.error('Failed to initialize:', error);
      alert('Failed to load data. Please check the console for details.');
    }
  }

  setupEventListeners() {
    // Layer toggles
    document.getElementById('layer1-toggle').addEventListener('change', (e) => {
      if (e.target.checked) {
        this.state.visibleLayers.add('metadata1');
      } else {
        this.state.visibleLayers.delete('metadata1');
      }
      this.render();
    });

    document.getElementById('layer2-toggle').addEventListener('change', (e) => {
      if (e.target.checked) {
        this.state.visibleLayers.add('metadata2');
      } else {
        this.state.visibleLayers.delete('metadata2');
      }
      this.render();
    });

    // Play/pause
    document.getElementById('play-pause').addEventListener('click', () => {
      this.togglePlayback();
    });

    // Window size
    document.getElementById('window-size').addEventListener('change', (e) => {
      this.state.windowSize = parseInt(e.target.value);
      this.stats.setWindowSize(this.state.windowSize);
      this.render();
    });

    // Playback delay
    document.getElementById('play-delay').addEventListener('change', (e) => {
      const delay = parseFloat(e.target.value);
      this.animator.setDelay(delay);
    });

    // Playback step size
    document.getElementById('play-step').addEventListener('change', (e) => {
      const stepSize = parseInt(e.target.value);
      this.animator.setStepSize(stepSize);
    });
  }

  onBrushChange(data) {
    // Called when user drags the brush on the timeline chart
    this.state.currentYear = data.year;
    this.state.windowSize = data.windowSize;

    // Update the window size input to reflect brush changes
    document.getElementById('window-size').value = data.windowSize;

    this.render();
  }

  onTimelineChange(year) {
    this.state.currentYear = year;
    // Update brush position on statistics chart (important for animator)
    this.stats.setYear(year);
    this.render();
  }

  onLocationSelect(location) {
    this.map.focusOnLocation(location);
  }

  onLocationClick(locationName, metadata) {
    // Get videos for the clicked location within current time window
    const windowStart = this.state.currentYear;
    const windowEnd = windowStart + this.state.windowSize;

    const videos = this.dataProcessor.getItemsForLocation(
      locationName,
      windowStart,
      windowEnd
    );

    console.log(`Clicked ${locationName}: found ${videos.length} videos`);

    // Show video panel with results
    this.videoPanel.show(locationName, videos, windowStart, windowEnd);
  }

  togglePlayback() {
    this.state.isPlaying = !this.state.isPlaying;
    const button = document.getElementById('play-pause');
    button.textContent = this.state.isPlaying ? 'Pause' : 'Play';

    if (this.state.isPlaying) {
      this.animator.play(this.state.currentYear, this.state.windowSize);
    } else {
      this.animator.pause();
    }
  }

  render() {
    const windowStart = this.state.currentYear;
    const windowEnd = windowStart + this.state.windowSize;

    // Update current window display
    document.getElementById('current-window').textContent =
      `Viewing: ${windowStart}-${windowEnd}`;

    // Get data for current window
    const windowData = this.dataProcessor.getWindowData(
      windowStart,
      windowEnd
    );

    // Update heatmaps
    if (this.state.visibleLayers.has('metadata1')) {
      this.heatmap1.update(windowData.metadata1);
    } else {
      this.heatmap1.clear();
    }

    if (this.state.visibleLayers.has('metadata2')) {
      this.heatmap2.update(windowData.metadata2);
    } else {
      this.heatmap2.clear();
    }

    // Update statistics
    this.stats.update(windowStart, windowEnd);
  }
}

// Initialize the application
const app = new InteractiveMapper();
app.init();
